import type { LevelDoc, PlacedObject, TerrainShape } from '../model/format.ts';
import { FORMAT_VERSION } from '../model/format.ts';
import { bakeLevel } from '../model/bake.ts';
import { gameData } from '../data/gameData.ts';
import { terrainStyles } from '../terrain/index.ts';
import { copyDecoration, copyShading, newLayer } from './actions.ts';
import { placementPose } from './catalog.ts';
import { uid, withoutDerived } from './store.ts';

function prefabByName(name: string): string | null {
  const gd = gameData();
  return Object.values(gd.prefabs).find((p) => p.name === name)?.guid ?? null;
}

/** Copy system objects (camera, level scripts, ...) from a template level so exports are complete. */
export function newLevel(name: string, template: LevelDoc | null, styleId?: string): LevelDoc {
  const id = `${name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'level'}-${Date.now().toString(36)}`;
  const level = newLayer('level', false);
  const fg = newLayer('foreground', false);
  const bg = newLayer('background', false);
  const objects: PlacedObject[] = [];
  const system: LevelDoc['system'] = {};
  if (template) {
    for (const role of ['camera', 'levelScripts', 'necessary'] as const) {
      const oid = template.system[role];
      const o = template.objects.find((x) => x.id === oid);
      if (!o) continue;
      const copy: PlacedObject = JSON.parse(JSON.stringify(o));
      copy.id = uid('o');
      copy.mods = copy.mods.filter((m) => !m.r || m.r.startsWith('asset:'));
      objects.push(copy);
      system[role] = copy.id;
    }
  } else {
    for (const [role, n] of [['camera', 'Main Camera'], ['levelScripts', 'LevelScripts'], ['necessary', 'NecessaryLevelComponents']] as const) {
      const g = prefabByName(n);
      if (!g) continue;
      const pose = placementPose(g);
      const o: PlacedObject = { id: uid('o'), name: n, prefab: g, x: 0, y: 0, rotation: pose.rotation, scale: pose.scale, mods: [] };
      objects.push(o);
      system[role] = o.id;
    }
  }
  const spawn = prefabByName('PlayerSpawnPoint');
  if (spawn) objects.push({ id: uid('o'), name: 'PlayerSpawnPoint', prefab: spawn, x: 3.5, y: 3, rotation: 0, scale: [1, 1], mods: [] });
  const style = styleId ?? terrainStyles()[0]?.id ?? '';
  const st = terrainStyles().find((s) => s.id === style);
  const ground: TerrainShape = {
    id: uid('t'),
    name: 'Ground',
    style,
    mode: 'add',
    rings: [[[-4, -6], [30, -6], [30, 2], [18, 2], [14, 4], [8, 4], [6, 2], [-4, 2]]],
    shading: copyShading(st?.defaultShading),
    decoration: copyDecoration(st?.defaultDecoration),
    targets: { level: level.id, foreground: fg.id, background: bg.id },
  };
  return {
    format: 'platformer-level',
    version: FORMAT_VERSION,
    id,
    name,
    createdAt: new Date().toISOString(),
    settings: template ? JSON.parse(JSON.stringify(template.settings)) : { backgroundColor: [0.38, 0.46, 0.65, 1], parallax: { prefab: null, mods: [], x: 0, y: 0 } },
    palette: [],
    layers: [bg, level, fg],
    terrains: style ? [ground] : [],
    objects,
    bodies: [],
    system,
  };
}

export function download(name: string, data: unknown) {
  const blob = new Blob([JSON.stringify(data, null, 1)], { type: 'application/json' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}

/** Crumble platform tiles are left out: the Unity importer runs the platforms' generators. */
export function exportForUnity(doc: LevelDoc) {
  download(`${doc.id}.baked.json`, bakeLevel(withoutDerived(doc)));
}

export function exportDocument(doc: LevelDoc) {
  download(`${doc.id}.level.json`, withoutDerived(doc));
}

export function readJsonFile(): Promise<any> {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,application/json';
    input.onchange = () => {
      const f = input.files?.[0];
      if (!f) return reject(new Error('no file'));
      f.text().then((t) => resolve(JSON.parse(t)), reject);
    };
    input.click();
  });
}

export function isLevelDoc(d: any): d is LevelDoc {
  return d && d.format === 'platformer-level' && Array.isArray(d.layers) && Array.isArray(d.objects);
}
